import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ExternalLink,
  GraduationCap,
  Play,
  Search,
  Sparkles,
  Youtube,
} from 'lucide-react';

import SEO from '../components/SEO';
import AdSlot from '../components/AdSlot';

const YOUTUBE_JSON_URL =
  'https://editoruma.github.io/portfolio/youtube.json';

interface VideoItem {
  id: string;
  title: string;
  videoId: string;
  description?: string;
  category?: string;
  duration?: string;
  publishedAt?: string;
}

interface ChannelData {
  name: string;
  description: string;
  youtubeUrl?: string;
}

interface YoutubeData {
  channel: ChannelData;
  videos: VideoItem[];
}

const EnglishWithUma: React.FC = () => {
  const [data, setData] = useState<YoutubeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const loadVideos = async () => {
      try {
        setLoading(true);

        const response = await fetch(YOUTUBE_JSON_URL);

        if (!response.ok) {
          throw new Error('Unable to fetch videos');
        }

        const json: YoutubeData = await response.json();

        setData(json);
      } catch (error) {
        console.error('English With Uma JSON Error:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, []);

  useEffect(() => {
    const footer = document.querySelector("footer");
    const header = document.querySelector("header");
    if (footer) footer.style.display = "none";
  if (header) header.style.display = "none";
    return () => {
      if (footer) footer.style.display = "";
      if (header) header.style.display = "";
    };
  }, []);

  const videos = useMemo(() => {
    if (!data?.videos) return [];

    return [...data.videos].sort((a, b) => {
      const dateA = new Date(a.publishedAt || '').getTime();
      const dateB = new Date(b.publishedAt || '').getTime();

      return dateB - dateA;
    });
  }, [data]);

  const categories = useMemo(() => {
    const values = videos
      .map((video) => video.category)
      .filter(Boolean) as string[];

    return ['All', ...Array.from(new Set(values))];
  }, [videos]);

  const filteredVideos = useMemo(() => {
    return videos.filter((video) => {
      const categoryMatch =
        activeCategory === 'All' ||
        video.category === activeCategory;

      const searchText = search.toLowerCase().trim();

      const searchMatch =
        !searchText ||
        video.title.toLowerCase().includes(searchText) ||
        video.description?.toLowerCase().includes(searchText) ||
        video.category?.toLowerCase().includes(searchText);

      return categoryMatch && searchMatch;
    });
  }, [videos, activeCategory, search]);

  const formatDate = (date?: string) => {
    if (!date) return '';

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      <SEO
        title="English With Uma - Learn English Online"
        description="Learn English with Uma through practical English speaking lessons, vocabulary, grammar, daily English conversations and useful communication tips."
        keywords={[
          'English With Uma',
          'learn English',
          'English speaking practice',
          'spoken English',
          'English vocabulary',
          'English grammar',
          'English conversation',
          'learn English online',
        ]}
        schemaType="page"
      />

      {/* ================= HERO ================= */}

      <section className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-blue-50">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-sky-200/40 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-32 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl" />

        <div className="relative container mx-auto px-4 py-20 md:py-28">
          <div className="max-w-5xl mx-auto text-center">

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-sky-200 shadow-sm text-sky-700 text-sm font-semibold mb-7">
              <Sparkles className="w-4 h-4" />
              Learn English • Speak Confidently • Grow
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-gray-900 leading-tight">
              English With{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-blue-600">
                Uma
              </span>
            </h1>

            <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Improve your English speaking, vocabulary, grammar and everyday
              communication with simple and practical lessons designed for
              real-life conversations.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-9">

              {data?.channel.youtubeUrl && (
                <a
                  href={data.channel.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <Youtube className="w-5 h-5" />
                  Visit YouTube Channel
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}

              <a
                href="#lessons"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white border border-gray-200 hover:border-sky-300 text-gray-800 font-semibold shadow-sm hover:shadow-md transition-all"
              >
                Explore Lessons
                <ArrowRight className="w-5 h-5" />
              </a>

            </div>
          </div>
        </div>
      </section>

      {/* ================= ABOUT ================= */}

      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">

          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">

            <div>
              <span className="text-sky-600 font-semibold text-sm uppercase tracking-wider">
                About English With Uma
              </span>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-6">
                Learn English in a Simple & Practical Way
              </h2>

              <p className="text-gray-600 leading-relaxed text-lg">
                English With Uma is created for learners who want to improve
                their English naturally and confidently. The lessons focus on
                practical English that you can actually use in everyday life.
              </p>

              <p className="text-gray-600 leading-relaxed text-lg mt-4">
                From speaking practice and vocabulary to grammar and common
                conversations, each lesson is designed to make learning
                English easier and more enjoyable.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mt-8">

                {[
                  'Everyday English',
                  'Speaking Practice',
                  'Vocabulary Building',
                  'Grammar Lessons',
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 p-4 rounded-xl bg-sky-50 border border-sky-100"
                  >
                    <CheckCircle2 className="w-5 h-5 text-sky-600 flex-shrink-0" />
                    <span className="font-medium text-gray-800">
                      {item}
                    </span>
                  </div>
                ))}

              </div>
            </div>

            <div className="relative">

              <div className="absolute inset-0 bg-gradient-to-br from-sky-200 to-blue-200 rounded-3xl rotate-3" />

              <div className="relative bg-gradient-to-br from-sky-500 to-blue-700 rounded-3xl p-8 md:p-10 text-white shadow-2xl">

                <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mb-7">
                  <GraduationCap className="w-9 h-9" />
                </div>

                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Your English Learning Journey Starts Here
                </h3>

                <p className="text-white/80 leading-relaxed">
                  Watch the lessons, practice regularly and build the
                  confidence to communicate naturally in English.
                </p>

                <div className="grid grid-cols-2 gap-4 mt-8">

                  <div className="bg-white/10 rounded-xl p-4">
                    <BookOpen className="w-6 h-6 mb-2" />
                    <div className="text-2xl font-bold">
                      {videos.length}+
                    </div>
                    <div className="text-sm text-white/70">
                      Video Lessons
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-4">
                    <Play className="w-6 h-6 mb-2" />
                    <div className="text-2xl font-bold">
                      {categories.length - 1}+
                    </div>
                    <div className="text-sm text-white/70">
                      Topics
                    </div>
                  </div>

                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= TOP AD ================= */}

      <section className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto py-6">
          <AdSlot slot="heroBanner" />
        </div>
      </section>

      {/* ================= VIDEO SECTION ================= */}

      <section
        id="lessons"
        className="py-16 md:py-20 bg-gray-50"
      >
        <div className="container mx-auto px-4">

          <div className="max-w-6xl mx-auto">

            <div className="text-center mb-10">
              <span className="text-sky-600 font-semibold text-sm uppercase tracking-wider">
                Video Lessons
              </span>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
                Learn English With Our Latest Videos
              </h2>

              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                Choose a lesson, watch the video and start improving your
                English today.
              </p>
            </div>

            {/* SEARCH */}

            <div className="max-w-xl mx-auto mb-8">
              <div className="relative">

                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search English lessons..."
                  className="w-full h-12 pl-12 pr-4 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent shadow-sm"
                />

              </div>
            </div>

            {/* CATEGORIES */}

            <div className="flex gap-2 overflow-x-auto pb-3 mb-10 justify-start md:justify-center">

              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                    activeCategory === category
                      ? 'bg-sky-600 text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-sky-300 hover:text-sky-600'
                  }`}
                >
                  {category}
                </button>
              ))}

            </div>

            {/* LOADING */}

            {loading && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div
                    key={item}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-200 animate-pulse"
                  >
                    <div className="aspect-video bg-gray-200" />

                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-1/3" />
                      <div className="h-5 bg-gray-200 rounded w-full" />
                      <div className="h-4 bg-gray-200 rounded w-4/5" />
                    </div>
                  </div>
                ))}

              </div>
            )}

            {/* ERROR */}

            {!loading && error && (
              <div className="max-w-xl mx-auto text-center bg-white rounded-2xl border border-red-200 p-8">

                <div className="text-red-500 font-semibold text-lg mb-2">
                  Unable to load videos
                </div>

                <p className="text-gray-600">
                  Please try refreshing the page after a few moments.
                </p>

              </div>
            )}

            {/* VIDEOS */}

            {!loading && !error && filteredVideos.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

                {filteredVideos.map((video, index) => (
                  <React.Fragment key={video.id}>

                    <Link
                      to={`/english-with-uma/watch/${video.videoId}`}
                      className="group block"
                    >

                      <article className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">

                        <div className="relative aspect-video overflow-hidden bg-gray-100">

                          <img
                            src={`https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`}
                            alt={video.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />

                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />

                          <div className="absolute inset-0 flex items-center justify-center">

                            <div className="w-14 h-14 rounded-full bg-white shadow-xl flex items-center justify-center scale-90 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-300">
                              <Play className="w-6 h-6 text-sky-600 fill-sky-600 ml-1" />
                            </div>

                          </div>

                          {video.duration && (
                            <span className="absolute bottom-3 right-3 bg-black/80 text-white text-xs font-medium px-2 py-1 rounded">
                              {video.duration}
                            </span>
                          )}

                        </div>

                        <div className="p-5">

                          <div className="flex items-center justify-between gap-3 mb-3">

                            {video.category && (
                              <span className="text-xs font-semibold text-sky-600 bg-sky-50 px-2.5 py-1 rounded-full">
                                {video.category}
                              </span>
                            )}

                            {video.publishedAt && (
                              <span className="text-xs text-gray-400">
                                {formatDate(video.publishedAt)}
                              </span>
                            )}

                          </div>

                          <h3 className="text-lg font-bold text-gray-900 line-clamp-2 group-hover:text-sky-600 transition-colors">
                            {video.title}
                          </h3>

                          {video.description && (
                            <p className="text-sm text-gray-500 leading-relaxed mt-2 line-clamp-2">
                              {video.description}
                            </p>
                          )}

                          <div className="flex items-center gap-2 mt-4 text-sm font-semibold text-sky-600">
                            Watch Lesson
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>

                        </div>

                      </article>

                    </Link>

                    {/* INLINE AD */}

                    {(index + 1) % 6 === 0 && (
                      <div className="md:col-span-2 lg:col-span-3 py-4">
                        <AdSlot slot="postInline" />
                      </div>
                    )}

                  </React.Fragment>
                ))}

              </div>
            )}

            {/* NO RESULTS */}

            {!loading &&
              !error &&
              filteredVideos.length === 0 && (
                <div className="text-center py-16">

                  <Search className="w-10 h-10 mx-auto text-gray-300 mb-4" />

                  <h3 className="text-xl font-bold text-gray-800">
                    No lessons found
                  </h3>

                  <p className="text-gray-500 mt-2">
                    Try another search or category.
                  </p>

                </div>
              )}

          </div>
        </div>
      </section>

      {/* ================= BOTTOM CTA ================= */}

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">

          <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-r from-sky-500 to-blue-700 p-8 md:p-12 text-center text-white shadow-xl">

            <Youtube className="w-10 h-10 mx-auto mb-4" />

            <h2 className="text-3xl md:text-4xl font-bold">
              Keep Learning. Keep Improving.
            </h2>

            <p className="text-white/80 max-w-2xl mx-auto mt-4">
              Subscribe to English With Uma on YouTube and continue your
              English learning journey with new lessons and practical tips.
            </p>

            {data?.channel.youtubeUrl && (
              <a
                href={data.channel.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-7 bg-white text-gray-900 px-7 py-3.5 rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Subscribe on YouTube
                <ExternalLink className="w-4 h-4" />
              </a>
            )}

          </div>

        </div>
      </section>
    </>
  );
};

export default EnglishWithUma;